import { useFieldApi } from 'informed';
import { taggedTemplateLiteral as _taggedTemplateLiteral, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import styled__default from 'styled-components';
import themes from '../../theming/themes.js';
import React, { memo, useState, useRef, useEffect } from 'react';
import withDefaultTheme from '../../hocs/withDefaultTheme.js';
import { format, parse, isValid } from 'date-fns';
import { DatePicker } from '@mantine/dates';
import { Popover, FocusTrap } from '@mantine/core';
import { useClickOutside } from '@mantine/hooks';
import { Language } from '../../types/Language.js';
import { TextInput, Icon, Button } from '../../index.js';

var _excluded = ["onChange", "theme", "initialValue", "field", "language", "disabled", "minDate", "maxDate", "validate", "maxLevel", "allowDeselect", "getDayAriaLabel", "hideOutsideDates", "highlightToday", "size", "required"];
var _templateObject, _templateObject2;
var DatePickerInputContainer = styled__default.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  justify-content: center;\n"])));
var DatePickerIconContainer = styled__default.button(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  position: absolute;\n  right: 15px;\n  top: 8px;\n  padding: 0;\n  background: none;\n  border: none;\n  margin-left: 0.25rem;\n  text-align: center;\n  cursor: pointer;\n  &:disabled {\n    cursor: not-allowed;\n  }\n  &:focus {\n    outline: 1px solid cornflowerblue;\n  }\n"])));
var ERROR_TEXT = {
  english: 'Please enter a valid date in MM/DD/YYYY format',
  spanish: 'Ingrese una fecha válida en el formato MM/DD/AAAA (mes/día/año)'
};
var REQUIRED_TEXT = {
  english: 'This field is required',
  spanish: 'Este campo es obligatorio'
};
var PLACEHOLDER_TEXT = {
  english: 'MM/DD/YYYY',
  spanish: 'MM/DD/AAAA'
};
var isValidWellFormattedDate = function isValidWellFormattedDate(value) {
  // enforce the value is in the correct format, can be parsed and is valid
  var parsedDate = parse(value, 'MM/dd/yyyy', new Date());
  var valid = isValid(parsedDate);
  return valid && format(parsedDate, 'MM/dd/yyyy') === value;
};
var NewDateInput = function NewDateInput(_ref) {
  var onChange = _ref.onChange,
    _ref$theme = _ref.theme,
    initialValue = _ref.initialValue,
    field = _ref.field,
    _ref$language = _ref.language,
    language = _ref$language === void 0 ? Language.ENGLISH : _ref$language,
    disabled = _ref.disabled,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    validate = _ref.validate,
    maxLevel = _ref.maxLevel,
    allowDeselect = _ref.allowDeselect,
    getDayAriaLabel = _ref.getDayAriaLabel,
    hideOutsideDates = _ref.hideOutsideDates,
    highlightToday = _ref.highlightToday,
    size = _ref.size,
    required = _ref.required,
    rest = _objectWithoutProperties(_ref, _excluded);
  // List of props to pass through to DatePicker unchanged
  var datePickerProps = {
    maxLevel: maxLevel,
    allowDeselect: allowDeselect,
    getDayAriaLabel: getDayAriaLabel,
    hideOutsideDates: hideOutsideDates,
    highlightToday: highlightToday,
    size: size
  };
  // This will spit out a warning because on first render there is no field yet rendered... idk why informed sucks so much but it does
  // also means this input will break if rendered outside a Form, as theres no safe way to guard against null/incomplete fieldApi
  var fieldApi = useFieldApi(field);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    opened = _useState2[0],
    setOpened = _useState2[1];
  var ref = useClickOutside(function () {
    return setOpened(false);
  });
  var inputRef = useRef(null);
  var initialCalendarDate = initialValue && isValidWellFormattedDate(initialValue) ? parse(initialValue, 'MM/dd/yyyy', new Date()) : null;
  var _useState3 = useState(initialCalendarDate),
    _useState4 = _slicedToArray(_useState3, 2),
    selectedCalendarDate = _useState4[0],
    setSelectedCalendarDate = _useState4[1];
  /** MASTER VALIDATOR -   */
  var oneValidateToRuleThemAll = function oneValidateToRuleThemAll(value, values) {
    if (value && !isValidWellFormattedDate(value)) {
      return language === Language.ENGLISH ? ERROR_TEXT.english : ERROR_TEXT.spanish;
    } else if (!value && required) {
      return language === Language.ENGLISH ? REQUIRED_TEXT.english : REQUIRED_TEXT.spanish;
    } else {
      return validate && validate(value, values);
    }
  };
  var handleOnCalendarChange = function handleOnCalendarChange(calendarDate) {
    setSelectedCalendarDate(calendarDate);
    fieldApi.setValue(calendarDate ? format(calendarDate, 'MM/dd/yyyy') : '');
    if (onChange) {
      onChange(calendarDate);
    }
    setOpened(false);
  };
  var handleOnInputChange = function handleOnInputChange(event) {
    // clean input of non / and digit characters
    var raw = event.target.value;
    var cleaned = raw.replace(/[^\d/]/g, '');
    //update the event value & local shortcut (so other change events doesn't get unclean value)
    event.target.value = cleaned;
    // set the field using field API
    // - only 'resets' cursor pos if you type invalid characters while editing earlier in the string FIXME: only usabilty gap here
    fieldApi.setValue(cleaned);
    // check for a real, well formatted date string
    if (isValidWellFormattedDate(cleaned)) {
      var dateValue = parse(cleaned, 'MM/dd/yyyy', new Date());
      // Call custom onChange if it exists with the Date value
      if (onChange) {
        onChange(dateValue, event);
      }
      // Update the date on the calendar
      setSelectedCalendarDate(dateValue);
    } else if (!cleaned) {
      // clears calendar picker date if you clear you input
      setSelectedCalendarDate(null);
    }
  };
  useEffect(function () {
    var handleKeyDown = function handleKeyDown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        setOpened(false);
      }
    };
    // @ts-ignore
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      // @ts-ignore
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [opened]);
  var validateMinMaxDateFormat = function validateMinMaxDateFormat(dateString) {
    if (isValidWellFormattedDate(dateString)) {
      throw new Error("Invalid date format: ".concat(dateString, ". Expected format: MM/dd/yyyy"));
    }
  };
  typeof minDate === 'string' && validateMinMaxDateFormat(minDate);
  typeof maxDate === 'string' && validateMinMaxDateFormat(maxDate);
  return /*#__PURE__*/React.createElement(DatePickerInputContainer, null, /*#__PURE__*/React.createElement(TextInput
  // FIXME: formatter causes accessibility issues with cursor moving around unexpectedly when editing beginning of date
  // @ts-ignore - Allow custom format (can't use if you go for the auto add missigng digits in cases like 5/5/2005 )
  // formatter={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
  , _extends({
    field: field,
    maxLength: 10,
    initialValue: initialValue,
    forwardedRef: inputRef,
    onChange: handleOnInputChange,
    theme: themes.lincoln,
    validate: oneValidateToRuleThemAll,
    validateOnMount: initialValue ? true : false,
    validateOnBlur: true,
    validateOnChange: true,
    placeholder: language === Language.ENGLISH ? PLACEHOLDER_TEXT.english : PLACEHOLDER_TEXT.spanish,
    disabled: disabled //TODO: make this aria-disabled when TextInput supports that FIXME: we should create a ticket for fixing TextInput
  }, rest)), /*#__PURE__*/React.createElement(Popover, {
    returnFocus: true,
    position: "bottom-end",
    styles: {
      dropdown: {
        padding: 0
      }
    },
    opened: opened
  }, /*#__PURE__*/React.createElement(Popover.Target, null, /*#__PURE__*/React.createElement(DatePickerIconContainer
  // TODO: this id doesn't set right and mantine breaks snapshots with its auto generated one
  , {
    id: "".concat(field, "-datepicker-open"),
    "aria-label": 'Choose a date using the datepicker',
    onClick: function onClick() {
      return setOpened(true);
    },
    disabled: disabled,
    type: 'button'
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "calendar",
    size: "medium",
    color: "secondary"
  }))), /*#__PURE__*/React.createElement(FocusTrap, {
    active: opened
  }, /*#__PURE__*/React.createElement(Popover.Dropdown, null, /*#__PURE__*/React.createElement(DatePicker, _extends({
    id: "".concat(field, "-datepicker"),
    minDate: typeof minDate === 'string' ? new Date(minDate) : minDate,
    maxDate: typeof maxDate === 'string' ? new Date(maxDate) : maxDate,
    ariaLabels: {
      nextMonth: 'Next month',
      previousMonth: 'Previous month'
    },
    "aria-label": 'Use arrow keys to navigate and press enter or space to select',
    "data-testid": 'date-picker',
    ref: ref,
    firstDayOfWeek: 0,
    nextIcon: /*#__PURE__*/React.createElement(Icon, {
      name: 'arrow-right',
      color: 'secondary'
    }),
    previousIcon: /*#__PURE__*/React.createElement(Icon, {
      name: 'arrow-left',
      color: 'secondary'
    }),
    styles: {
      calendarHeader: {
        marginTop: '0.5rem',
        marginBottom: '0.25rem'
      },
      calendarHeaderLevel: {
        fontSize: '1.25rem',
        fontWeight: 'bold'
      },
      day: {
        fontSize: '1.125rem',
        padding: '1.25rem'
      },
      weekdaysRow: {
        background: themes["default"].palette.grayDark
      },
      weekday: {
        color: themes["default"].palette.white,
        paddingBottom: 0,
        padding: '0.25rem',
        fontSize: '1.125rem'
      },
      yearsListControl: {
        fontSize: '1.125rem'
      },
      monthsListControl: {
        fontSize: '1.125rem'
      }
    },
    onDateChange: setSelectedCalendarDate,
    onChange: function onChange(value) {
      return handleOnCalendarChange(value);
    },
    value: selectedCalendarDate,
    date: selectedCalendarDate || new Date()
  }, datePickerProps)), /*#__PURE__*/React.createElement("div", {
    className: 'd-flex justify-content-end mx-3 mt-3 mb-2'
  }, /*#__PURE__*/React.createElement(Button, {
    id: "".concat(field, "-datepicker-close"),
    size: 'small',
    buttonType: 'secondary',
    onClick: function onClick() {
      return setOpened(false);
    },
    type: 'button'
  }, "Close"))))));
};
var _NewDateInput = withDefaultTheme(/*#__PURE__*/memo(NewDateInput));

export default _NewDateInput;
